import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'cs' : 'en';
    i18n.changeLanguage(newLang);
  };

  const scrollToSection = (sectionId: string) => {
    if (location.pathname !== '/') {
      window.REACT_APP_NAVIGATE('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-[rgba(26,29,35,0.95)] backdrop-blur-xl shadow-lg'
          : 'bg-[rgba(26,29,35,0.85)] backdrop-blur-md'
      }`}
      style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.08)' }}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <img 
              src="https://public.readdy.ai/ai/img_res/de7a14ac-d629-4bc7-b74b-4536ef7ed887.png" 
              alt="NEXNOVA SOLUTIONS" 
              className="h-14 w-auto"
            />
            <span className="text-white font-bold text-2xl hidden sm:inline whitespace-nowrap">
              NEXNOVA SOLUTIONS
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-10">
            <button
              onClick={() => scrollToSection('home')}
              className="text-white text-base font-medium hover:text-[#00d4ff] transition-colors duration-300 relative group whitespace-nowrap"
            >
              {t('nav.home')}
              <span className="absolute bottom-[-4px] left-0 w-0 h-[2px] bg-[#00d4ff] group-hover:w-full transition-all duration-300"></span>
            </button>
            <button
              onClick={() => scrollToSection('services')}
              className="text-white text-base font-medium hover:text-[#00d4ff] transition-colors duration-300 relative group whitespace-nowrap"
            >
              {t('nav.services')}
              <span className="absolute bottom-[-4px] left-0 w-0 h-[2px] bg-[#00d4ff] group-hover:w-full transition-all duration-300"></span>
            </button>
            <button
              onClick={() => scrollToSection('about')}
              className="text-white text-base font-medium hover:text-[#00d4ff] transition-colors duration-300 relative group whitespace-nowrap"
            >
              {t('nav.about')}
              <span className="absolute bottom-[-4px] left-0 w-0 h-[2px] bg-[#00d4ff] group-hover:w-full transition-all duration-300"></span>
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className="text-white text-base font-medium hover:text-[#00d4ff] transition-colors duration-300 relative group whitespace-nowrap"
            >
              {t('nav.contact')}
              <span className="absolute bottom-[-4px] left-0 w-0 h-[2px] bg-[#00d4ff] group-hover:w-full transition-all duration-300"></span>
            </button>
          </nav>

          {/* Right Section */}
          <div className="flex items-center gap-4">
            {/* Language Switcher */}
            <button
              onClick={toggleLanguage}
              className="px-4 py-2 rounded-full bg-[rgba(255,255,255,0.1)] backdrop-blur-sm text-white text-sm font-medium hover:bg-[rgba(255,255,255,0.15)] transition-all duration-300 whitespace-nowrap"
            >
              {i18n.language === 'en' ? 'EN | CZ' : 'CZ | EN'}
            </button>

            {/* Contact Button - Desktop */}
            <button
              onClick={() => scrollToSection('contact')}
              className="hidden lg:flex items-center gap-2 px-6 py-3 rounded-xl bg-[rgba(255,255,255,0.08)] backdrop-blur-sm text-white text-sm font-medium hover:bg-[rgba(0,212,255,0.2)] hover:shadow-[0_0_20px_rgba(0,212,255,0.3)] transition-all duration-300 whitespace-nowrap"
            >
              <i className="ri-phone-line text-lg"></i>
              {t('nav.getInTouch')}
            </button>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden text-white text-2xl w-10 h-10 flex items-center justify-center"
            >
              <i className={isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden py-6 border-t border-[rgba(255,255,255,0.08)]">
            <nav className="flex flex-col gap-4">
              <button
                onClick={() => scrollToSection('home')}
                className="text-white text-base font-medium hover:text-[#00d4ff] transition-colors duration-300 text-left whitespace-nowrap"
              >
                {t('nav.home')}
              </button>
              <button
                onClick={() => scrollToSection('services')}
                className="text-white text-base font-medium hover:text-[#00d4ff] transition-colors duration-300 text-left whitespace-nowrap"
              >
                {t('nav.services')}
              </button>
              <button
                onClick={() => scrollToSection('about')}
                className="text-white text-base font-medium hover:text-[#00d4ff] transition-colors duration-300 text-left whitespace-nowrap"
              >
                {t('nav.about')}
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className="text-white text-base font-medium hover:text-[#00d4ff] transition-colors duration-300 text-left whitespace-nowrap"
              >
                {t('nav.contact')}
              </button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}